/**
 * BrowserProbe — reports what this browser will and will not let m3cull do.
 *
 * Its single job is capability detection. It exists because "does the folder
 * picker exist" and "why doesn't it exist" are different questions, and the
 * second one decides what we can usefully tell the photographer.
 *
 * Brave is the case that makes this worth a class: it is Chromium, so it looks
 * capable, but it ships the File System Access API switched off behind
 * brave://flags/#file-system-access-api. Telling a Brave user to "use Brave"
 * is worse than saying nothing.
 */

export type BlockReason =
  /** Safari and Firefox — no folder picker at all, and none coming. */
  | 'no-api'
  /** Brave with the flag off — one setting away from working. */
  | 'brave-flag-off'
  /**
   * A phone or tablet. No mobile browser exposes the folder picker yet — not
   * even Chrome for Android, which makes "use Chrome" the wrong thing to say.
   */
  | 'mobile'
  /** Ancient Chromium without createImageBitmap/OffscreenCanvas. */
  | 'no-image-pipeline';

export interface Capabilities {
  canOpenFolders: boolean;
  canProcessImages: boolean;
  isBrave: boolean;
  isMobile: boolean;
  blockedBy: BlockReason | null;
}

type BraveNavigator = Navigator & { brave?: { isBrave: () => Promise<boolean> } };
type UaDataNavigator = Navigator & { userAgentData?: { mobile?: boolean } };

export class BrowserProbe {
  /** Brave exposes navigator.brave.isBrave(); every other browser does not. */
  static async isBrave(): Promise<boolean> {
    const nav = navigator as BraveNavigator;
    if (!nav.brave?.isBrave) return false;
    try {
      return await nav.brave.isBrave();
    } catch {
      return false;
    }
  }

  static get hasDirectoryPicker(): boolean {
    return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
  }

  static get hasImagePipeline(): boolean {
    return typeof createImageBitmap === 'function' && typeof OffscreenCanvas === 'function';
  }

  /**
   * Phone or tablet.
   *
   * Chromium reports this directly. Everything else needs the user-agent, plus
   * one extra check for iPadOS, which claims to be a Mac and only gives itself
   * away by having a touchscreen.
   */
  static get isMobile(): boolean {
    if (typeof navigator === 'undefined') return false;

    const uaData = (navigator as UaDataNavigator).userAgentData;
    if (typeof uaData?.mobile === 'boolean') {
      if (uaData.mobile) return true;
    }

    const ua = navigator.userAgent;
    if (/Android|iPhone|iPod/i.test(ua)) return true;
    if (/iPad/i.test(ua)) return true;

    // iPadOS 13+ masquerades as desktop Safari.
    return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  }

  static async detect(): Promise<Capabilities> {
    const canOpenFolders = BrowserProbe.hasDirectoryPicker;
    const canProcessImages = BrowserProbe.hasImagePipeline;
    const isBrave = await BrowserProbe.isBrave();
    const isMobile = BrowserProbe.isMobile;

    // Order matters: on a phone the reason is the platform, not the browser the
    // reader chose, so telling an Android Chrome user to "use Chrome" is wrong.
    let blockedBy: BlockReason | null = null;
    if (!canProcessImages) blockedBy = 'no-image-pipeline';
    else if (!canOpenFolders) {
      if (isMobile) blockedBy = 'mobile';
      else blockedBy = isBrave ? 'brave-flag-off' : 'no-api';
    }

    return { canOpenFolders, canProcessImages, isBrave, isMobile, blockedBy };
  }
}
